/**
 * Google Analytics Service
 *
 * Centralised analytics wrapper around gtag.js that:
 *   1. Uses Consent Mode v2 — gtag.js loads in index.html with analytics_storage denied.
 *   2. `initGA()` grants consent after the user accepts cookies.
 *   3. Provides type-safe `trackEvent` and `trackPageView` helpers.
 *
 * No analytics data is collected until the user has accepted cookies.
 */

/* ═══════════════════════════════════════════════════════════════════
   Global type augmentation for gtag
   ═══════════════════════════════════════════════════════════════════ */

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

/* ═══════════════════════════════════════════════════════════════════
   Event Catalog — all custom event names + params
   ═══════════════════════════════════════════════════════════════════ */

export interface AnalyticsEventMap {
  // ── Dashboard / Command Center ──────────────────────────────
  schedule_dialog_opened: void;
  export_dialog_opened: { source: 'dashboard' | 'schedule_detail' };
  block_chart_navigated: void;
  help_dialog_opened: void;
  onboarding_cta_clicked: { step: 'positions' | 'members' };

  // ── Schedule Generation ─────────────────────────────────────
  schedule_generated: { positions_count: number; members_count: number; duration_hours: number };
  schedule_generation_failed: { error_message: string };

  // ── Schedule Detail ─────────────────────────────────────────
  schedule_accepted: { schedule_id: string };
  schedule_archived: { schedule_id: string };
  schedule_edit_started: { schedule_id: string };
  schedule_edit_saved: { schedule_id: string };

  // ── Export ──────────────────────────────────────────────────
  schedule_exported_copy: { group_by: string; positions_count: number };
  schedule_exported_whatsapp: { group_by: string; positions_count: number };
  export_group_by_changed: { group_by: 'time' | 'position' };

  // ── Positions ──────────────────────────────────────────────
  position_created: { has_time_windows: boolean; demand_modifier: number };
  position_updated: { position_id: string };
  position_deleted: { position_id: string };
  position_form_advanced_toggled: { expanded: boolean };

  // ── Members ────────────────────────────────────────────────
  member_created: { seniority: string; is_leader: boolean };
  member_updated: { member_id: string };
  member_deleted: { member_id: string };
  member_form_advanced_toggled: { expanded: boolean };

  // ── Block Chart ────────────────────────────────────────────
  chart_viewed: { schedules_count: number };
  chart_schedule_clicked: { schedule_id: string };

  // ── Settings ───────────────────────────────────────────────
  settings_saved: { changed_fields: string[] };
  seniority_toggled: { enabled: boolean };

  // ── Info Hub ───────────────────────────────────────────────
  info_tab_changed: { tab: string };

  // ── Consent ────────────────────────────────────────────────
  cookie_consent_accepted: void;
}

/** Union of every event name we track. */
export type AnalyticsEvent = keyof AnalyticsEventMap;

/* ═══════════════════════════════════════════════════════════════════
   Internal state
   ═══════════════════════════════════════════════════════════════════ */

let consentGranted = false;

/* ═══════════════════════════════════════════════════════════════════
   Public API
   ═══════════════════════════════════════════════════════════════════ */

/**
 * Grant analytics consent via Consent Mode v2.
 *
 * The gtag.js script is loaded in index.html with `analytics_storage: 'denied'`
 * by default. This function upgrades consent to `'granted'`, allowing GA to
 * start collecting data. Safe to call multiple times — only the first call
 * has an effect.
 */
export function initGA(): void {
  if (consentGranted) return;

  if (!window.gtag) {
    if (import.meta.env.DEV) {
      console.info('[Analytics] gtag not found — skipping consent grant (dev mode).');
    }
    return;
  }

  window.gtag('consent', 'update', {
    analytics_storage: 'granted',
  });

  consentGranted = true;
}

/**
 * Send a custom analytics event.
 * Silently no-ops if GA has not been initialised (e.g. no consent).
 */
export function trackEvent<E extends AnalyticsEvent>(
  name: E,
  ...args: AnalyticsEventMap[E] extends void ? [] : [params: AnalyticsEventMap[E]]
): void {
  if (!consentGranted || !window.gtag) return;
  const params = args[0];
  if (params) {
    window.gtag('event', name, params);
  } else {
    window.gtag('event', name);
  }
}

/**
 * Track a virtual page view (for SPA route transitions).
 */
export function trackPageView(path: string, title?: string): void {
  if (!consentGranted || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
  });
}

/**
 * Check whether analytics consent has been granted.
 * Useful for components that need to gate behavior.
 */
export function isGAInitialised(): boolean {
  return consentGranted;
}
